import { Check } from "lucide-react";

export interface OnboardingTrailStep {
  /** 2-digit number string, e.g. "01". */
  num: string;
  /** Step name, e.g. "Empresa". */
  label: string;
  /** Short narrative sub-line, e.g. "Decinos qué tenés". */
  sub: string;
  /** Human-readable time estimate, e.g. "30s" or "2m". */
  time: string;
}

interface OnboardingTrailProps {
  steps: OnboardingTrailStep[];
  /** 1-indexed current step. Steps before it are "done", after it are "upcoming". */
  current: number;
}

export function OnboardingTrail({
  steps,
  current,
}: OnboardingTrailProps): JSX.Element {
  const pct = Math.round(((current - 1) / steps.length) * 100);
  return (
    <div className="fx-onb-trail">
      <div className="fx-onb-trail-head">
        <span>Progreso</span>
        <span className="pct">{pct}%</span>
      </div>
      <div className="fx-onb-trail-list">
        {steps.map((s, i) => {
          const idx = i + 1;
          const state =
            idx < current ? "done" : idx === current ? "current" : "upcoming";
          return (
            <div key={s.num} className={`fx-onb-trail-item ${state}`}>
              <span className="num">
                {state === "done" ? <Check aria-hidden /> : s.num}
              </span>
              <span className="label">
                {s.label}
                <div className="sub">{s.sub}</div>
              </span>
              <span className="when">
                {state === "done"
                  ? "Listo"
                  : state === "current"
                    ? "Ahora"
                    : `~ ${s.time}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Canonical 4-step trail used by the base-onboarding screens. */
export const BASE_ONBOARDING_TRAIL: OnboardingTrailStep[] = [
  { num: "01", label: "Empresa", sub: "Decinos qué tenés", time: "30s" },
  { num: "02", label: "Datos de DGI", sub: "Traemos tus datos", time: "1m" },
  { num: "03", label: "Tu identidad", sub: "Verificamos quién sos", time: "1m" },
  { num: "04", label: "Quién sigue", sub: "Vos o tu contador", time: "30s" },
];

/** 3-step trail used by the DGI configuration sub-flow (Situación → Modo → Configuración técnica). */
export const DGI_CONFIG_TRAIL: OnboardingTrailStep[] = [
  { num: "01", label: "Situación", sub: "Dónde estás parado", time: "1m" },
  { num: "02", label: "Modo", sub: "Cómo lo configuramos", time: "2m" },
  { num: "03", label: "Configuración", sub: "Te dejamos listo", time: "3m" },
];

/**
 * Optional 4th step appended to DGI_CONFIG_TRAIL for users who already
 * emit electronic invoices ("already_issuer") and therefore have legacy
 * CFEs to import on top of the standard DGI configuration.
 */
export const CFE_IMPORT_TRAIL_STEP: OnboardingTrailStep = {
  num: "04",
  label: "CFE anteriores",
  sub: "Importá tus comprobantes",
  time: "1m",
};

/**
 * 4th step appended to DGI_CONFIG_TRAIL for users who need a fresh
 * certificate ("scratch" or "issuer_need_regularization"). Surfaces
 * the cert acquisition phase as a distinct trail beat instead of
 * folding it into "Configuración".
 */
export const CERT_REQUEST_TRAIL_STEP: OnboardingTrailStep = {
  num: "04",
  label: "Certificado",
  sub: "Tu certificado digital",
  time: "2m",
};

/**
 * Build the right-column trail for the DGI sub-flow given the user's
 * situation choice (and whether they're migrating legacy CFEs).
 *
 *   - scratch / issuer_need_regularization → 4 rows with the certificate
 *     phase placed *before* DGI configuration (the actual traversal
 *     order in the wizard: cert request kicks off in parallel so DGI
 *     and Abitab identity check run together rather than back-to-back).
 *   - already_issuer + isMigratingCfes → append CFE_IMPORT_TRAIL_STEP.
 *   - already_issuer without migration → base 3-step trail.
 *   - null (no choice yet) → base 3-step trail; we don't claim a 4th
 *     row we might not need.
 */
/**
 * The CERTIFICATE phase drawn as its own trail.
 *
 * The DGI trail (getDgiConfigTrail) describes the whole journey — Situación,
 * Acceso DGI, Modo, Certificado — and the certificate is one row of it. That
 * is right while a tenant is moving through the journey, and wrong once they
 * are inside the certificate itself: the pane kept showing Situación and
 * Acceso DGI, phases already finished, while the eyebrow above it said
 * "Certificado digital · paso 03 de 04". The label named one process and the
 * numbers counted another.
 *
 * The rows differ by PATH, which is the part that is easy to miss: getting a
 * certificate yourself and having Fiscalo get it for you are not the same
 * process with a different label, they are different lengths. The managed path
 * has a request we file and a counter visit we cannot do for you; the manual
 * path has neither, and ends the moment the file is uploaded.
 */
export function getCertificateTrail(
  path: "manual" | "fiscalo" | null,
): OnboardingTrailStep[] {
  if (path === "fiscalo") {
    return [
      { num: "01", label: "Modo", sub: "Cómo conseguís el certificado", time: "1m" },
      { num: "02", label: "Solicitud", sub: "La generamos en ID Digital", time: "2m" },
      { num: "03", label: "Abitab", sub: "Pagás y validás identidad", time: "1 visita" },
      { num: "04", label: "Certificado", sub: "Lo instalamos por vos", time: "automático" },
    ];
  }
  // Manual, and the fallback before a path is chosen: the Modo screen itself
  // is row 1, so a tenant who has not answered yet still sees where they are.
  return [
    { num: "01", label: "Modo", sub: "Cómo conseguís el certificado", time: "1m" },
    { num: "02", label: "Lo conseguís", sub: "Abitab, Correo o Antel", time: "1 visita" },
    { num: "03", label: "Certificado", sub: "Lo subís a Fiscalo", time: "2m" },
  ];
}

export function getDgiConfigTrail(opts: {
  situationChoice: "scratch" | "issuer_need_regularization" | "already_issuer" | null;
  isMigratingCfes: boolean;
  /**
   * Scratch only, phase-aware. While homologación is still pending (the user
   * has just created the DGI e-Factura user and is about to be handed to our
   * team), the cert/CAE phase is a multi-week team handoff away — advertising
   * "Modo → Certificado" as steps 03/04 overstates how close it is. In that
   * phase we show a 3-row trail ending in "Homologación" (our team) instead.
   * Post-homologación the wizard resumes on the cert screens, which pass the
   * default (false) and get the full cert-focused trail below.
   */
  homologacionPending?: boolean;
}): OnboardingTrailStep[] {
  if (opts.situationChoice === "scratch") {
    if (opts.homologacionPending) {
      // Pre-homologación self-flow: the only self step is Configuración DGI;
      // then it's handed to our team. Cert/CAE come after homologación.
      return [
        DGI_CONFIG_TRAIL[0]!, // 01 Situación
        { num: "02", label: "Acceso DGI", sub: "Tu usuario y permisos", time: "3m" },
        {
          num: "03",
          label: "Homologación",
          sub: "La gestiona nuestro equipo",
          time: "unos días",
        },
      ];
    }
    // scratch is DGI-first (DGI → cert → CAE): a brand-new emisor has no
    // DGI-authorized identity to sign a certificate for until DGI authorizes
    // it, so Configuración DGI comes before the certificate phase.
    return [
      DGI_CONFIG_TRAIL[0]!, // 01 Situación
      { num: "02", label: "Acceso DGI", sub: "Tu usuario y permisos", time: "3m" },
      { num: "03", label: "Modo", sub: "Cómo conseguís el certificado", time: "2m" },
      { num: "04", label: "Certificado", sub: "Tu certificado digital", time: "2m" },
    ];
  }
  if (opts.situationChoice === "issuer_need_regularization") {
    // Regularization is cert-first: the emisor is already registered, so the
    // cert request kicks off before DGI and the Abitab visit runs in parallel.
    return [
      DGI_CONFIG_TRAIL[0]!, // 01 Situación
      DGI_CONFIG_TRAIL[1]!, // 02 Modo (cert choice — Lo hago yo / Que Fiscalo lo gestione)
      { num: "03", label: "Certificado", sub: "Tu certificado digital", time: "2m" },
      { num: "04", label: "Configuración DGI", sub: "Te dejamos listo en DGI", time: "3m" },
    ];
  }
  if (opts.situationChoice === "already_issuer") {
    // DGI-first, then the certificate — the same shape as scratch. This used
    // to be the bare 3-row DGI_CONFIG_TRAIL, whose "Modo" means "how do we
    // configure DGI" and which has no certificate row at all, because an
    // already_issuer tenant was assumed to hold a certificate and never
    // reached the cert question. Now that they are asked (spec cc0cd3d9), that
    // trail sent them from Configuración (03) back to Modo (02) — progress
    // visibly going backwards on a step they were moving forward through.
    const trail: OnboardingTrailStep[] = [
      DGI_CONFIG_TRAIL[0]!, // 01 Situación
      { num: "02", label: "Acceso DGI", sub: "Tu usuario y permisos", time: "3m" },
      { num: "03", label: "Modo", sub: "Cómo conseguís el certificado", time: "2m" },
      { num: "04", label: "Certificado", sub: "Tu certificado digital", time: "2m" },
    ];
    return opts.isMigratingCfes
      ? [...trail, { ...CFE_IMPORT_TRAIL_STEP, num: "05" }]
      : trail;
  }
  return DGI_CONFIG_TRAIL;
}

/**
 * Map a wizard viewStep (pre-offset logical numbering: situación 2, dgi 3,
 * certChoice 4, certContent 5/6) to its 1-indexed row in the DGI sub-flow trail.
 *
 *   - scratch is DGI-first: Situación (2), Configuración DGI (3), Modo (4),
 *     Certificado (5 & 6).
 *   - regularization is cert-first (2 → 4 → 5 → 6 → 3 → 7+): Situación (2),
 *     Modo (4), Certificado (5 & 6), Configuración DGI (3).
 *   - already_issuer is DGI-first like scratch: Situación (2),
 *     Configuración DGI (3), Modo (4), Certificado (5 & 6).
 *   - null: linear over the bare 3-row trail.
 */
export function getDgiTrailCurrentStep(
  viewStep: number,
  situationChoice: "scratch" | "issuer_need_regularization" | "already_issuer" | null,
): number {
  if (situationChoice === "scratch") {
    if (viewStep === 2) return 1;
    if (viewStep === 3) return 2;
    if (viewStep === 4) return 3;
    if (viewStep === 5 || viewStep === 6) return 4;
    return 1;
  }
  if (situationChoice === "issuer_need_regularization") {
    if (viewStep === 2) return 1;
    if (viewStep === 4) return 2;
    if (viewStep === 5 || viewStep === 6) return 3;
    if (viewStep === 3) return 4;
    return 4;
  }
  if (situationChoice === "already_issuer") {
    // Same ordering as scratch: Situación (2), Configuración DGI (3),
    // Modo (4), Certificado (5 & 6). The old mapping put viewStep 4 on row 2
    // and DGI on row 3, which is what made the cert question read as a step
    // BACKWARDS from the DGI screen the user had just finished.
    if (viewStep === 2) return 1;
    if (viewStep === 3) return 2;
    if (viewStep === 4) return 3;
    if (viewStep === 5 || viewStep === 6) return 4;
    return 1;
  }
  // null situation: linear mapping over the bare 3-row trail
  if (viewStep === 2) return 1;
  if (viewStep === 4) return 2;
  if (viewStep === 3) return 3;
  if (viewStep === 5 || viewStep === 6) return 3;
  return 1;
}
